/**
 * Terrassements : les bâtiments conçus pour un sol plat (parking, retenue
 * collinaire, garage à dameuses, bâtiments du village) sont posés sur une
 * plateforme aplanie, raccordée au relief naturel par un talus (déblai côté
 * amont, remblai côté aval), comme sur un vrai chantier de station. Sans elle,
 * sur une pente, le relief recouvre une partie du bitume du parking ou de
 * l'eau de la retenue.
 *
 * Le relief terrassé est une copie réservée au rendu : la simulation, le
 * serveur et la validation des constructions gardent le relief naturel
 * (`store.hf`). Le terrain dessiné, le décor, les skieurs et les bâtiments se
 * posent sur la copie. Les gares et pylônes, placés sur le relief naturel,
 * sont protégés : le talus s'efface à leurs abords.
 */
import { BUILDINGS, Heightfield, type Building, type BuildingKind } from '@snowchain/shared';

/**
 * Bâtiments posés sur une plateforme. Les autres sont dessinés pour la pente
 * (soubassement ou pilotis) : restaurant, snack, poste de secours, caisses,
 * enneigeur, lampadaire.
 */
const PADDED: ReadonlySet<BuildingKind> = new Set<BuildingKind>([
  'chalet_accueil',
  'shop_rental',
  'welcome_area',
  'hotel',
  'residence',
  'ski_school',
  'groomer_garage',
  'parking',
  'reservoir',
]);

/** Bande aplanie autour de l'emprise : débords de toit, perrons, congères (m). */
const MARGIN = 1.5;
/** Un bâtiment reste enfoncé de 25 cm sous la moyenne du relief de son emprise. */
const SINK = 0.25;
/** La plateforme passe sous le sol du bâtiment, sans scintillement entre les deux (m). */
const BELOW = 0.06;
/** Largeur du talus : 4 m par mètre de dénivelé au bord de la plateforme, bornée. */
const TALUS_PER_M = 4;
const TALUS_MIN = 6;
const TALUS_MAX = 20;
/** Au-delà de ce poids, l'échantillon est sur la plateforme et n'est jamais atténué. */
const ON_PAD = 0.999;

export interface Rect {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
}

/** Zone protégée (gare, pylône) : centre et rayon, en mètres. */
export type KeepOut = [x: number, z: number, radius: number];

type Seated = Pick<Building, 'kind' | 'x' | 'z' | 'rot'>;

/** Assise d'un bâtiment sur un relief : moyenne du centre et des coins de l'emprise, légèrement enfoncée. */
export function seatHeight(hf: Heightfield, b: Seated): number {
  const [w, d] = BUILDINGS[b.kind].footprint;
  const c = Math.cos(b.rot);
  const s = Math.sin(b.rot);
  const pts: [number, number][] = [
    [0, 0],
    [-w / 2, -d / 2],
    [w / 2, -d / 2],
    [w / 2, d / 2],
    [-w / 2, d / 2],
  ];
  let sum = 0;
  for (const [px, pz] of pts) sum += hf.height(b.x + px * c + pz * s, b.z - px * s + pz * c);
  return sum / pts.length - SINK;
}

const smooth = (t: number) => t * t * (3 - 2 * t);

export class Earthworks {
  /** Relief de rendu : relief naturel et plateformes. */
  readonly hf: Heightfield;
  /** Poids de la plateforme retenue par échantillon (0 = relief naturel). */
  private readonly weight: Float32Array;
  /** Hauteur visée par échantillon, celle de la plateforme qui l'emporte. */
  private readonly target: Float32Array;
  private touched: number[] = [];
  private readonly pads = new Map<number, number>();
  private key = '';

  constructor(private readonly natural: Heightfield) {
    this.hf = new Heightfield(natural.data.slice());
    this.hf.minH = natural.minH;
    this.hf.maxH = natural.maxH;
    this.hf.waterLevel = natural.waterLevel;
    this.weight = new Float32Array(natural.data.length);
    this.target = new Float32Array(natural.data.length);
  }

  /** Hauteur où poser un bâtiment : sa plateforme, sinon la pente telle qu'elle est dessinée. */
  seat(b: Building): number {
    return this.pads.get(b.id) ?? seatHeight(this.hf, b);
  }

  /**
   * Recalcule les plateformes quand les bâtiments terrassés ou les zones
   * protégées changent. Renvoie le rectangle du relief modifié (anciennes et
   * nouvelles plateformes), ou null si rien n'a changé.
   */
  apply(buildings: Iterable<Building>, keep: readonly KeepOut[]): Rect | null {
    const list = [...buildings].filter((b) => PADDED.has(b.kind));
    const key = `${list.map((b) => `${b.id}:${b.kind}:${b.x}:${b.z}:${b.rot}`).join('|')}#${keep.map((k) => k.join(':')).join('|')}`;
    if (key === this.key) return null;
    this.key = key;

    const { res, cell, half } = this.hf;
    const nat = this.natural.data;
    const data = this.hf.data;
    const weight = this.weight;
    const target = this.target;
    const rect: Rect = { x0: Infinity, z0: Infinity, x1: -Infinity, z1: -Infinity };
    const grow = (k: number) => {
      const x = -half + (k % res) * cell;
      const z = -half + Math.floor(k / res) * cell;
      if (x < rect.x0) rect.x0 = x;
      if (x > rect.x1) rect.x1 = x;
      if (z < rect.z0) rect.z0 = z;
      if (z > rect.z1) rect.z1 = z;
    };

    // Relief naturel rétabli là où se trouvaient les plateformes précédentes.
    for (const k of this.touched) {
      data[k] = nat[k] as number;
      weight[k] = 0;
      grow(k);
    }
    this.touched = [];
    this.pads.clear();

    const index = (x: number) => Math.round((x + half) / cell);
    const clampIdx = (i: number) => Math.max(0, Math.min(res - 1, i));

    for (const b of list) {
      const base = seatHeight(this.natural, b);
      this.pads.set(b.id, base);
      const [w, d] = BUILDINGS[b.kind].footprint;
      const c = Math.cos(b.rot);
      const s = Math.sin(b.rot);
      const hw = w / 2 + MARGIN;
      const hd = d / 2 + MARGIN;
      // Talus proportionnel au plus fort dénivelé relevé au bord de la plateforme.
      let drop = 0;
      for (const [px, pz] of [
        [-hw, -hd],
        [0, -hd],
        [hw, -hd],
        [hw, 0],
        [hw, hd],
        [0, hd],
        [-hw, hd],
        [-hw, 0],
      ] as [number, number][]) {
        drop = Math.max(drop, Math.abs(this.natural.height(b.x + px * c + pz * s, b.z - px * s + pz * c) - base));
      }
      const talus = Math.min(TALUS_MAX, Math.max(TALUS_MIN, drop * TALUS_PER_M));
      const reach = Math.hypot(hw, hd) + talus;
      const i0 = clampIdx(index(b.x - reach) - 1);
      const i1 = clampIdx(index(b.x + reach) + 1);
      const j0 = clampIdx(index(b.z - reach) - 1);
      const j1 = clampIdx(index(b.z + reach) + 1);
      for (let j = j0; j <= j1; j++) {
        const dz = -half + j * cell - b.z;
        for (let i = i0; i <= i1; i++) {
          const dx = -half + i * cell - b.x;
          // Repère local du bâtiment (rotation inverse de celle du rendu).
          const px = dx * c - dz * s;
          const pz = dx * s + dz * c;
          const out = Math.hypot(Math.max(0, Math.abs(px) - w / 2), Math.max(0, Math.abs(pz) - d / 2));
          let wgt: number;
          if (out <= MARGIN) {
            // L'emprise elle-même l'emporte sur la marge d'un voisin.
            wgt = 1 - out * 1e-4;
          } else {
            const t = (out - MARGIN) / talus;
            if (t >= 1) continue;
            wgt = 1 - smooth(t);
          }
          const k = j * res + i;
          if (weight[k] === 0) this.touched.push(k);
          if (wgt > (weight[k] as number)) {
            weight[k] = wgt;
            target[k] = base - BELOW;
          }
        }
      }
    }

    // Abords des gares et pylônes : le talus s'efface, la plateforme reste entière.
    for (const [kx, kz, r] of keep) {
      const i0 = clampIdx(index(kx - r));
      const i1 = clampIdx(index(kx + r));
      const j0 = clampIdx(index(kz - r));
      const j1 = clampIdx(index(kz + r));
      for (let j = j0; j <= j1; j++) {
        for (let i = i0; i <= i1; i++) {
          const k = j * res + i;
          const wk = weight[k] as number;
          if (wk === 0 || wk >= ON_PAD) continue;
          const dist = Math.hypot(-half + i * cell - kx, -half + j * cell - kz);
          if (dist < r) weight[k] = wk * smooth(dist / r);
        }
      }
    }

    for (const k of this.touched) {
      const h = nat[k] as number;
      data[k] = h + ((target[k] as number) - h) * Math.min(1, weight[k] as number);
      grow(k);
    }
    return rect.x0 <= rect.x1 ? rect : null;
  }
}
